import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthContext';
import ProjectFolderSetupNotice from '@/components/projects/ProjectFolderSetupNotice';
import BydaResultFileList from '@/components/BydaResultFileList';
import BydaResultPdfPreview from '@/components/BydaResultPdfPreview';
import BydaUsageSummary from '@/components/BydaUsageSummary';
import BydaYvwSewerInfoDialog from '@/components/BydaYvwSewerInfoDialog';
import ProjectManagementEvidence from '@/components/projects/ProjectManagementEvidence';
import { ProjectDocumentStatusPanel } from '@/components/projects/ProjectDocumentStatusPanel';
import { ProjectMemoryPanel } from '@/components/projects/ProjectMemoryPanel';
import { localProjectFolderUrl, refreshProjectFolderStatus } from '@/services/projectFolderStatusService';
import { BellRing, ChevronLeft, FileSearch, FolderPlus, Pencil, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import {
  normalizeLookupPostCode,
  normalizeSuburbLookupValue,
  searchSuburbOptions,
  searchSuburbOptionsByPostCode,
  type SuburbLookupRow,
} from '@/services/suburbLookup';
import { confirmDeletionByTyping } from '@/utils/deleteConfirmation';
import { formatSuburbLabel } from '@/utils/suburbText';
import { hasRequiredRoleAccess } from '@/utils/swaRole';
import {
  BYDA_WORKPLACE_OPTIONS,
  buildBydaDraftPolygon,
  createBydaDraftFromProject,
  getDefaultBydaDesignActivityCodes,
  mapWorkplaceSelectionsToPayload,
  normalizeDraftField,
  syncBydaCodes,
  toInputString,
  toggleBydaCode,
  tryBuildPolygonFromDraftPoints,
  type BydaPointDraft,
  type BydaDraftForm,
} from '@/utils/bydaDraft';
import { formatBydaDisplayDate } from '@/utils/bydaDates';
import {
  bydaService,
  type BydaEnquiryDetail,
  type BydaFormOption,
  type BydaFormOptions,
  type BydaResultDocument,
  type BydaUsagePolicySummary,
  type BydaWorkspaceEnquiryRecord,
  type BydaYvwSewerInfo,
  type CreateBydaEnquiryInput,
} from '@/services/bydaService';
import { buildBydaBlockedMessage, buildBydaConfirmationMessage } from '@/utils/bydaPolicy';
import { Button, DetailActionMenu, DetailFieldList, FormSection, Modal, Select, StatusBadge } from '@/ui/components';
import BydaMapCapture from './BydaMapCapture';
import '@/pages/dataGridFocus.css';

/*
 * Project detail screen.
 *
 * Plain-English view:
 * - Shows one project record.
 * - Lets the user edit it, restore an archived project, create its SharePoint folder, or soft-delete it.
 * - Uses the normalized project-by-id API so the page does not need to know raw database column names.
 */
interface DetailField {
  label: string;
  value: React.ReactNode;
  wrap?: boolean;
  className?: string;
}

type ProjectEditDraft = {
  projectName: string;
  status: string;
  siteAddress: string;
  suburb: string;
  postCode: string;
  council: string;
  clientEmail: string;
  clientPhone: string;
  councilEngineerEmail: string;
  description: string;
  siteId: string;
  driveId: string;
  folderItemId: string;
  folderPath: string;
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid var(--color-border)',
  fontSize: '14px',
  lineHeight: 1.4,
  color: 'var(--color-text)',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-surface)',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: '96px',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const bydaOptionGridStyle: React.CSSProperties = {
  display: 'grid',
  gap: '10px',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
};

const bydaOptionCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
  fontSize: '14px',
};

function formatBydaOptionLabel(option: BydaFormOption) {
  return option.label || option.code;
}

function hasBydaSelection(values: string[], code: string) {
  return values.includes(code);
}

function createDraftFromProject(project: any): ProjectEditDraft {
  return {
    projectName: toInputString(project?.ProjectName),
    status: toInputString(project?.Status || 'Active'),
    siteAddress: toInputString(project?.SiteAddress),
    suburb: toInputString(project?.Suburb),
    postCode: toInputString(project?.PostCode),
    council: toInputString(project?.Council),
    clientEmail: toInputString(project?.ClientEmail),
    clientPhone: toInputString(project?.ClientPhone || project?.ClientMobile),
    councilEngineerEmail: toInputString(project?.CouncilEngineerEmail),
    description: toInputString(project?.Description),
    siteId: toInputString(project?.SiteId),
    driveId: toInputString(project?.DriveId),
    folderItemId: toInputString(project?.FolderItemId),
    folderPath: toInputString(project?.FolderPath),
  };
}

function buildCouncilOptions(rows: SuburbLookupRow[], currentValue?: string | null): string[] {
  const seen = new Set<string>();
  const options: string[] = [];

  const push = (value?: string | null) => {
    const normalized = String(value || '').trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) return;
    seen.add(key);
    options.push(normalized);
  };

  rows.forEach((row) => push(row.Council));
  push(currentValue);

  return options;
}

const PROJECT_STATUS_OPTIONS = ['Active', 'Inactive', 'On Hold', 'Completed', 'Cancelled'];

function parseOptionalIntegerInput(value: string, label: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`${label} must be a valid number.`);
  }
  return Math.trunc(parsed);
}

function formatFieldValue(value: any) {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && (value.includes('T') || value.includes('/'))) {
    try {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      }
    } catch {}
  }
  return String(value);
}

function formatBydaStatusLabel(value: unknown) {
  const normalized = String(value || '').trim();
  if (!normalized) return 'Unknown';
  return normalized
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatBydaDateTime(value?: string | null) {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizeProjectStatusValue(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function isProjectArchivedLike(project: any): boolean {
  if (!project) return false;
  const normalizedStatus = normalizeProjectStatusValue(project.Status);
  return normalizedStatus === 'cancelled'
    || normalizedStatus === 'canceled'
    || normalizedStatus === 'deleted'
    || Number(project.IsDeleted ?? 0) === 1
    || Boolean(project.DeletedDate);
}

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const canNotifyProgress = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['pm', 'admin']);
  const canViewDocumentStatus = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['pm', 'admin']);
  const [project, setProject] = useState<any>(null);
  const [draft, setDraft] = useState<ProjectEditDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [folderProvisioning, setFolderProvisioning] = useState(false);
  const [folderSetupRefresh, setFolderSetupRefresh] = useState(0);
  const [folderRefreshing, setFolderRefreshing] = useState(false);
  const [statusPublishing, setStatusPublishing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [suburbOptions, setSuburbOptions] = useState<SuburbLookupRow[]>([]);
  const [suburbLookupMessage, setSuburbLookupMessage] = useState('');
  const [bydaDraft, setBydaDraft] = useState<BydaDraftForm | null>(null);
  const [bydaAutoLocateKey, setBydaAutoLocateKey] = useState(0);
  const [bydaFormOptions, setBydaFormOptions] = useState<BydaFormOptions | null>(null);
  const [bydaFormOptionsLoading, setBydaFormOptionsLoading] = useState(false);
  const [bydaFormOptionsError, setBydaFormOptionsError] = useState<string | null>(null);
  const [bydaEnquiries, setBydaEnquiries] = useState<BydaWorkspaceEnquiryRecord[]>([]);
  const [bydaPolicy, setBydaPolicy] = useState<BydaUsagePolicySummary | null>(null);
  const [bydaLoading, setBydaLoading] = useState(false);
  const [bydaLoadError, setBydaLoadError] = useState<string | null>(null);
  const [bydaSubmitting, setBydaSubmitting] = useState(false);
  const [bydaSubmitError, setBydaSubmitError] = useState<string | null>(null);
  const [bydaSubmitNotice, setBydaSubmitNotice] = useState<string | null>(null);
  const [selectedBydaEnquiryId, setSelectedBydaEnquiryId] = useState<number | null>(null);
  const [bydaDetail, setBydaDetail] = useState<BydaEnquiryDetail | null>(null);
  const [bydaDetailLoading, setBydaDetailLoading] = useState(false);
  const [bydaDetailError, setBydaDetailError] = useState<string | null>(null);
  const [bydaPreviewDocument, setBydaPreviewDocument] = useState<BydaResultDocument | null>(null);
  const [bydaFiling, setBydaFiling] = useState(false);
  const [bydaFilingFeedback, setBydaFilingFeedback] = useState<string | null>(null);
  const [bydaFilingFeedbackTone, setBydaFilingFeedbackTone] = useState<'error' | 'success'>('success');
  const [bydaYvwSewerInfo, setBydaYvwSewerInfo] = useState<BydaYvwSewerInfo | null>(null);
  const [bydaYvwSewerLoading, setBydaYvwSewerLoading] = useState(false);
  const [bydaYvwSewerError, setBydaYvwSewerError] = useState<string | null>(null);
  const [projectDocumentStatusOpen, setProjectDocumentStatusOpen] = useState(false);
  const [centralEvidenceOpen, setCentralEvidenceOpen] = useState(false);
  const councilOptions = buildCouncilOptions(suburbOptions, draft?.council);
  const projectIsArchived = isProjectArchivedLike(project);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    void loadProjectDetails();
  }, [isAuthenticated, user, projectId]);

  useEffect(() => {
    if (!isEditing || !draft) {
      setSuburbOptions([]);
      setSuburbLookupMessage('');
      return;
    }

    const suburb = draft.suburb.trim();
    if (suburb.length < 2) {
      setSuburbOptions([]);
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const matches = await searchSuburbOptions(suburb, 8);
        if (!active) return;
        setSuburbOptions(matches);

        const exactMatch = matches.find(
          (row) => normalizeSuburbLookupValue(row.Suburb) === normalizeSuburbLookupValue(suburb)
        );
        if (exactMatch) {
          setDraft((current) => (
            current ? {
              ...current,
              suburb: exactMatch.Suburb || current.suburb,
              postCode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
                ? String(exactMatch.PostCode)
                : current.postCode,
              council: exactMatch.Council || current.council,
            } : current
          ));
          setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
        }
      } catch {
        if (!active) return;
        setSuburbOptions([]);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [draft?.suburb, isEditing]);

  useEffect(() => {
    if (!project) {
      setBydaDraft(null);
      setBydaAutoLocateKey(0);
      setBydaFormOptions(null);
      setBydaFormOptionsError(null);
      return;
    }

    setBydaDraft(createBydaDraftFromProject(project));
    setBydaAutoLocateKey((current) => current + 1);
    setBydaFormOptions(null);
    setBydaFormOptionsError(null);
  }, [
    project?.ProjectId,
    project?.ProjectCode,
    project?.SiteAddress,
    project?.Suburb,
    project?.PostCode,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !user || !project?.ProjectId) {
      setBydaEnquiries([]);
      setBydaLoadError(null);
      return;
    }

    void loadBydaEnquiries(project.ProjectId);
  }, [isAuthenticated, user, project?.ProjectId]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setBydaPolicy(null);
      return;
    }

    void bydaService.getPolicySummary()
      .then((policy) => setBydaPolicy(policy))
      .catch(() => setBydaPolicy(null));
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (selectedBydaEnquiryId && bydaEnquiries.some((item) => item.bydaEnquiryId === selectedBydaEnquiryId)) {
      return;
    }

    setSelectedBydaEnquiryId(bydaEnquiries[0]?.bydaEnquiryId || null);
  }, [bydaEnquiries, selectedBydaEnquiryId]);

  useEffect(() => {
    if (!selectedBydaEnquiryId) {
      setBydaDetail(null);
      setBydaDetailError(null);
      setBydaYvwSewerInfo(null);
      setBydaYvwSewerError(null);
      return;
    }

    let cancelled = false;
    setBydaYvwSewerInfo(null);
    setBydaYvwSewerError(null);
    void loadBydaDetail(selectedBydaEnquiryId, {
      refreshRemote: false,
    }).then((detail) => {
      if (cancelled || !detail) return;
      setBydaDetail(detail);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedBydaEnquiryId]);

  const loadProjectDetails = async (background = false) => {
    if (!projectId) return;

    if (!background) {
      setLoading(true);
      setError(null);
    } else {
      setSaveError(null);
    }

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const foundProject = await azureSqlService.getProjectById(projectId, { useCache: false });

      if (!foundProject) {
        if (background) {
          setSaveError('Project not found during folder refresh.');
          return;
        }
        setError('Project not found');
        return;
      }

      setProject(foundProject);
      if (!isEditing) {
        setDraft(createDraftFromProject(foundProject));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (background) {
        setSaveError(`Failed to refresh project: ${errorMessage}`);
      } else {
        setError(`Failed to load project: ${errorMessage}`);
      }
    } finally {
      if (!background) setLoading(false);
    }
  };

  const loadBydaEnquiries = async (targetProjectId?: number | string | null) => {
    const resolvedProjectId = Math.trunc(Number(targetProjectId || project?.ProjectId) || 0);
    if (resolvedProjectId <= 0) {
      setBydaEnquiries([]);
      setBydaLoadError(null);
      return;
    }

    setBydaLoading(true);
    setBydaLoadError(null);

    try {
      const records = await bydaService.listEnquiries({
        projectId: resolvedProjectId,
        limit: 8,
      });
      setBydaEnquiries(records);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setBydaLoadError(`Failed to load BYDA enquiries: ${message}`);
    } finally {
      setBydaLoading(false);
    }
  };

  const loadBydaDetail = async (bydaEnquiryId: number, options?: { refreshRemote?: boolean }) => {
    setBydaDetailLoading(true);
    setBydaDetailError(null);

    try {
      const detail = await bydaService.getEnquiryDetail(bydaEnquiryId, {
        refreshRemote: options?.refreshRemote ?? true,
        includeResults: true,
      });
      setBydaDetail(detail);
      return detail;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setBydaDetail(null);
      setBydaDetailError(`Failed to load BYDA detail: ${message}`);
      return null;
    } finally {
      setBydaDetailLoading(false);
    }
  };

  const handleBydaFileResults = async () => {
    if (!selectedBydaEnquiryId || bydaFiling) {
      return;
    }

    setBydaFiling(true);
    setBydaFilingFeedback(null);
    setBydaDetailError(null);
    try {
      const detail = await bydaService.fileEnquiryResults(selectedBydaEnquiryId);
      setBydaDetail(detail);
      setBydaFilingFeedbackTone(detail.failedCount && detail.failedCount > 0 ? 'error' : 'success');
      setBydaFilingFeedback(
        detail.failedCount && detail.failedCount > 0
          ? `Filed ${detail.uploadedCount || 0} BYDA result file${detail.uploadedCount === 1 ? '' : 's'}, with ${detail.failedCount} item${detail.failedCount === 1 ? '' : 's'} still needing attention.`
          : `Filed ${detail.uploadedCount || 0} BYDA result file${detail.uploadedCount === 1 ? '' : 's'} to the project folder.`
      );
      setBydaEnquiries((current) => current.map((item) => (
        item.bydaEnquiryId === selectedBydaEnquiryId && detail.record
          ? detail.record
          : item
      )));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setBydaFilingFeedbackTone('error');
      setBydaFilingFeedback(message);
    } finally {
      setBydaFiling(false);
    }
  };

  const handleFetchBydaYvwSewerInfo = async () => {
    if (!selectedBydaEnquiryId || bydaYvwSewerLoading) {
      return;
    }

    setBydaYvwSewerLoading(true);
    setBydaYvwSewerError(null);
    try {
      const sewerInfo = await bydaService.getYvwSewerInfo(selectedBydaEnquiryId);
      setBydaYvwSewerInfo(sewerInfo);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setBydaYvwSewerInfo(null);
      setBydaYvwSewerError(message);
    } finally {
      setBydaYvwSewerLoading(false);
    }
  };

  const handleStartEditing = () => {
    if (!project || folderProvisioning || folderRefreshing) return;
    setDraft(createDraftFromProject(project));
    setSuburbOptions([]);
    setSuburbLookupMessage('');
    setSaveError(null);
    setSaveNotice(null);
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setDraft(project ? createDraftFromProject(project) : null);
    setSuburbOptions([]);
    setSuburbLookupMessage('');
    setSaveError(null);
    setSaveNotice(null);
    setIsEditing(false);
  };

  const setDraftField = (field: keyof ProjectEditDraft, value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  };

  const setBydaField = <K extends keyof BydaDraftForm>(field: K, value: BydaDraftForm[K]) => {
    setBydaDraft((current) => (current ? { ...current, [field]: value } : current));
  };

  const setBydaPoints = (points: BydaPointDraft[]) => {
    setBydaDraft((current) => (
      current ? {
        ...current,
        points,
      } : current
    ));
  };

  const resetBydaDraft = () => {
    if (!project) return;
    setBydaDraft(createBydaDraftFromProject(project));
    setBydaAutoLocateKey((current) => current + 1);
    setBydaSubmitError(null);
    setBydaSubmitNotice(null);
  };

  const updateBydaCodeSelection = (field: 'activityTypes', code: string, enabled: boolean) => {
    setBydaDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        [field]: toggleBydaCode(current[field], code, enabled),
      };
    });
  };

  const updateBydaWorkplaceSelection = (code: string, enabled: boolean) => {
    setBydaDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        workplaceSelections: toggleBydaCode(current.workplaceSelections, code, enabled),
      };
    });
  };

  const bydaDraftShape = bydaDraft ? tryBuildPolygonFromDraftPoints(bydaDraft.points) : null;
  const bydaDraftShapeKey = JSON.stringify(bydaDraftShape?.coordinates || null);

  useEffect(() => {
    if (!project?.ProjectId || !bydaDraft) {
      setBydaFormOptions(null);
      setBydaFormOptionsError(null);
      setBydaFormOptionsLoading(false);
      return;
    }

    let cancelled = false;
    setBydaFormOptionsLoading(true);
    setBydaFormOptionsError(null);

    void bydaService.getFormOptions({
      projectId: Math.trunc(Number(project.ProjectId) || 0),
      isPlanningJob: bydaDraft.isPlanningJob,
      shape: bydaDraftShape,
    })
      .then((options) => {
        if (cancelled) return;
        setBydaFormOptions(options);
        setBydaDraft((current) => {
          if (!current) return current;
          return {
            ...current,
            activityTypes: syncBydaCodes(current.activityTypes, options.activityTypes, {
              fallback: getDefaultBydaDesignActivityCodes(options.activityTypes),
              allowEmpty: false,
            }),
            authorityId: options.authorityOptions.some((option) => String(option.id) === current.authorityId)
              ? current.authorityId
              : '',
          };
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setBydaFormOptions(null);
        setBydaFormOptionsError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        if (cancelled) return;
        setBydaFormOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [project?.ProjectId, bydaDraft?.isPlanningJob, bydaDraftShapeKey]);

  const hydrateSuburbLookup = async (rawSuburb: string) => {
    const suburb = rawSuburb.trim();
    if (suburb.length < 2) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptions(suburb, 8);
      setSuburbOptions(matches);

      const exactMatch = matches.find(
        (row) => normalizeSuburbLookupValue(row.Suburb) === normalizeSuburbLookupValue(suburb)
      );
      if (!exactMatch) {
        setSuburbLookupMessage('No exact suburb match found yet. Keep typing or pick from the list.');
        return;
      }

      setDraft((current) => (
        current ? {
          ...current,
          suburb: exactMatch.Suburb || current.suburb,
          postCode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
            ? String(exactMatch.PostCode)
            : current.postCode,
          council: exactMatch.Council || current.council,
        } : current
      ));
      setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const hydratePostCodeLookup = async (rawPostCode: string) => {
    const postCode = normalizeLookupPostCode(rawPostCode);
    if (postCode.length < 3) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptionsByPostCode(postCode, 20);
      setSuburbOptions(matches);

      if (matches.length === 0) {
        setSuburbLookupMessage('No suburb match found for that postcode yet.');
        return;
      }

      const exactSuburbMatch = matches.find(
        (row) => normalizeSuburbLookupValue(row.Suburb) === normalizeSuburbLookupValue(draft?.suburb)
      );
      const selectedMatch = exactSuburbMatch || (matches.length === 1 ? matches[0] : null);

      if (!selectedMatch) {
        setDraft((current) => (
          current ? {
            ...current,
            postCode,
          } : current
        ));
        setSuburbLookupMessage('Multiple suburbs match that postcode. Pick the suburb from the list.');
        return;
      }

      setDraft((current) => (
        current ? {
          ...current,
          suburb: selectedMatch.Suburb || current.suburb,
          postCode: selectedMatch.PostCode !== null && selectedMatch.PostCode !== undefined
            ? String(selectedMatch.PostCode)
            : postCode,
          council: selectedMatch.Council || current.council,
        } : current
      ));
      setSuburbLookupMessage(`Suburb${selectedMatch.Council ? ', postcode, and council' : ' and postcode'} filled from lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const renderDraftField = (
    field: keyof ProjectEditDraft,
    options?: { multiline?: boolean; type?: string; selectOptions?: string[] }
  ) => {
    if (!draft) return null;

    if (options?.selectOptions) {
      return (
        <select
          value={draft[field]}
          onChange={(event) => setDraftField(field, event.target.value)}
          style={inputStyle}
        >
          {options.selectOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (options?.multiline) {
      return (
        <textarea
          value={draft[field]}
          onChange={(event) => setDraftField(field, event.target.value)}
          style={textareaStyle}
        />
      );
    }

    return (
      <input
        type={options?.type || 'text'}
        value={draft[field]}
        onChange={(event) => setDraftField(field, event.target.value)}
        style={inputStyle}
      />
    );
  };

  const handleSaveProject = async () => {
    if (!project || !draft) return;

    setSaving(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const nextPostCode = parseOptionalIntegerInput(draft.postCode, 'Postcode');
      const currentPostCode = project?.PostCode == null || project?.PostCode === ''
        ? null
        : Math.trunc(Number(project.PostCode));

      const payload = {
        ...(normalizeDraftField(draft.projectName) !== normalizeDraftField(toInputString(project.ProjectName)) ? { projectName: draft.projectName } : {}),
        ...(normalizeDraftField(draft.status) !== normalizeDraftField(toInputString(project.Status || 'Active')) ? { status: draft.status } : {}),
        ...(normalizeDraftField(draft.siteAddress) !== normalizeDraftField(toInputString(project.SiteAddress)) ? { siteAddress: draft.siteAddress } : {}),
        ...(normalizeDraftField(draft.suburb) !== normalizeDraftField(toInputString(project.Suburb)) ? { suburb: draft.suburb } : {}),
        ...(nextPostCode !== currentPostCode ? { postCode: nextPostCode } : {}),
        ...(normalizeDraftField(draft.council) !== normalizeDraftField(toInputString(project.Council)) ? { council: draft.council } : {}),
        ...(normalizeDraftField(draft.clientEmail) !== normalizeDraftField(toInputString(project.ClientEmail)) ? { clientEmail: draft.clientEmail } : {}),
        ...(normalizeDraftField(draft.clientPhone) !== normalizeDraftField(toInputString(project.ClientPhone || project.ClientMobile)) ? { clientPhone: draft.clientPhone } : {}),
        ...(normalizeDraftField(draft.councilEngineerEmail) !== normalizeDraftField(toInputString(project.CouncilEngineerEmail)) ? { councilEngineerEmail: draft.councilEngineerEmail } : {}),
        ...(normalizeDraftField(draft.description) !== normalizeDraftField(toInputString(project.Description)) ? { description: draft.description } : {}),
        ...(normalizeDraftField(draft.siteId) !== normalizeDraftField(toInputString(project.SiteId)) ? { siteId: draft.siteId } : {}),
        ...(normalizeDraftField(draft.driveId) !== normalizeDraftField(toInputString(project.DriveId)) ? { driveId: draft.driveId } : {}),
        ...(normalizeDraftField(draft.folderItemId) !== normalizeDraftField(toInputString(project.FolderItemId)) ? { folderItemId: draft.folderItemId } : {}),
        ...(normalizeDraftField(draft.folderPath) !== normalizeDraftField(toInputString(project.FolderPath)) ? { folderPath: draft.folderPath } : {}),
      };

      if (Object.keys(payload).length === 0) {
        setSaveNotice('No project changes to save.');
        setIsEditing(false);
        return;
      }

      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.updateProject(project.ProjectId, payload);
      await loadProjectDetails();
      setSaveNotice('Project details updated.');
      setIsEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!project || deleting || saving || folderProvisioning) return;

    const projectNumber = String(project.ProjectCode || project.ProjectId || '').trim();
    if (!projectNumber) {
      setSaveError('Project number is required before deleting this project.');
      return;
    }

    if (!confirmDeletionByTyping({
      entityLabel: `project ${projectNumber}`,
      confirmationLabel: 'project number',
      identifiers: [projectNumber],
      softDelete: true,
      extraMessage: 'This only marks the project row as deleted in the database. SharePoint files/folders are not removed, and deletion will be blocked if linked tasks or populated SharePoint subfolders still exist.',
    })) {
      return;
    }

    setDeleting(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.deleteProject(project.ProjectId);
      setDeleting(false);
      navigate('/projects');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(message);
      setDeleting(false);
    }
  };

  const handleProvisionProjectFolder = async () => {
    if (!project || folderProvisioning || saving || deleting) return;

    setFolderProvisioning(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.provisionProjectFolders(project.ProjectId);
      setFolderSetupRefresh(value => value + 1);
      await loadProjectDetails();

      const sharePoint = result?.sharePoint || null;
      const integrationStatus = String(sharePoint?.integrationStatus || '').trim().toLowerCase();
      const configuredButEmpty = integrationStatus === 'configured' || sharePoint?.projectFolder;
      const folderPath = sharePoint?.projectFolder?.folderPath || result?.civilProjectRoot || '';
      const detail = folderPath ? ` ${folderPath}` : '';
      const message = String(sharePoint?.message || '').trim();

      if (result?.job?.state === 'queued' || result?.job?.state === 'running' || integrationStatus === 'queued') {
        setSaveNotice(message || 'Project folder setup is running in the background. Check setup status below.');
      } else if (result?.job?.state === 'needs_attention') {
        setSaveNotice(message || 'Folder setup needs attention. Existing files are retained.');
      } else if (configuredButEmpty) {
        setSaveNotice(`Project folder provisioned.${detail}`);
      } else if (message) {
        setSaveNotice(`Project folder provisioning returned: ${message}`);
      } else {
        setSaveNotice('Project folder provisioning completed.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setFolderSetupRefresh(value => value + 1);
      setSaveNotice(`Folder setup result could not be confirmed. Check its status before retrying. ${message}`);
    } finally {
      setFolderProvisioning(false);
    }
  };

  const handleRefreshProjectFolder = async () => {
    if (!project || folderRefreshing || folderProvisioning || saving || deleting || restoring) return;

    setFolderRefreshing(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.refreshProjectFolderLink(project.ProjectId);
      await loadProjectDetails();
      const folderPath = String(result?.folderPath || result?.sharePoint?.folderPath || '').trim();
      setSaveNotice(folderPath
        ? `Folder link refreshed: ${folderPath}`
        : 'Folder link refreshed from SharePoint.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(`Failed to refresh folder link: ${message}`);
    } finally {
      setFolderRefreshing(false);
    }
  };

  const handleRestoreProject = async () => {
    if (!project || restoring || saving || deleting || folderProvisioning || !isProjectArchivedLike(project)) return;

    const projectNumber = String(project.ProjectCode || project.ProjectId || '').trim();
    const confirmed = window.confirm(
      `Restore project ${projectNumber}?\n\nThis will clear the archived or soft-deleted flags and return the project to Active so it appears in the normal projects list again.`
    );
    if (!confirmed) {
      return;
    }

    setRestoring(true);
    setSaveError(null);
    setSaveNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.updateProject(project.ProjectId, { status: 'Active' });
      await loadProjectDetails();
      setSaveNotice(`Project ${projectNumber} restored to Active.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(`Failed to restore project: ${message}`);
    } finally {
      setRestoring(false);
    }
  };

  const handleSubmitBydaEnquiry = async () => {
    if (!project || !bydaDraft || bydaSubmitting) return;

    setBydaSubmitting(true);
    setBydaSubmitError(null);
    setBydaSubmitNotice(null);

    try {
      if (bydaFormOptionsLoading) {
        throw new Error('BYDA options are still loading.');
      }
      if (!bydaFormOptions) {
        throw new Error(bydaFormOptionsError || 'BYDA options are unavailable right now.');
      }

      const activityTypes = bydaDraft.activityTypes;
      const { locationTypes, locationsInRoad } = mapWorkplaceSelectionsToPayload(bydaDraft.workplaceSelections);
      if (activityTypes.length === 0) {
        throw new Error('Choose at least one BYDA activity type.');
      }
      if (bydaDraft.workplaceSelections.length === 0) {
        throw new Error('Choose at least one workplace location.');
      }

      const payload: CreateBydaEnquiryInput = {
        projectId: Math.trunc(Number(project.ProjectId) || 0),
        userReference: normalizeDraftField(bydaDraft.userReference),
        digStartAt: bydaDraft.digStartAt,
        digEndAt: bydaDraft.digEndAt,
        shape: buildBydaDraftPolygon(bydaDraft.points),
        isPlanningJob: bydaDraft.isPlanningJob,
        activityTypes,
        authorityId: bydaDraft.authorityId ? Number(bydaDraft.authorityId) : undefined,
        notes: normalizeDraftField(bydaDraft.notes),
        locationTypes,
        locationsInRoad,
        isSandboxTest: bydaDraft.isSandboxTest,
        userTimezone: normalizeDraftField(bydaDraft.userTimezone),
        address: {
          line1: normalizeDraftField(bydaDraft.addressLine1),
          locality: normalizeDraftField(bydaDraft.locality),
          postcode: normalizeDraftField(bydaDraft.postcode),
          state: normalizeDraftField(bydaDraft.state),
          country: normalizeDraftField(bydaDraft.country),
        },
      };

      const preview = await bydaService.previewEnquiry(payload);
      setBydaPolicy(preview.policy);

      if (!preview.canSubmit) {
        throw new Error(buildBydaBlockedMessage(preview));
      }

      const confirmed = window.confirm(buildBydaConfirmationMessage(preview));
      if (!confirmed) {
        setBydaSubmitNotice('BYDA enquiry cancelled before any API call was sent.');
        return;
      }

      const record = await bydaService.createEnquiry({
        ...payload,
        confirmSubmission: true,
      });
      setSelectedBydaEnquiryId(record?.bydaEnquiryId || null);
      setBydaFilingFeedback(null);
      await loadBydaEnquiries(project.ProjectId);
      const refreshedPolicy = await bydaService.getPolicySummary().catch(() => preview.policy);
      setBydaPolicy(refreshedPolicy);
      setBydaSubmitNotice(
        record?.remoteEnquiryId
          ? `BYDA enquiry created. Remote enquiry #${record.remoteEnquiryId} is now linked to this project.`
          : 'BYDA enquiry created and logged against this project.'
      );
    } catch (err) {
      void bydaService.getPolicySummary().then((policy) => setBydaPolicy(policy)).catch(() => undefined);
      const message = err instanceof Error ? err.message : 'Unknown error';
      setBydaSubmitError(message);
    } finally {
      setBydaSubmitting(false);
    }
  };

  const bydaStatusTone = bydaEnquiries[0]?.localStatus || (bydaSubmitting ? 'submitting' : 'ready');
  const bydaSelectedRecord = bydaEnquiries.find((item) => item.bydaEnquiryId === selectedBydaEnquiryId) || null;
  const bydaResultDocuments = bydaDetail?.resultDocuments || [];
  const bydaWebhookEvents = bydaDetail?.webhookEvents || [];

  const detailFields: DetailField[] = project ? [
    { label: 'Project ID', value: project.ProjectId || 'Not set' },
    { label: 'Project Code', value: project.ProjectCode || 'Not set' },
    {
      label: 'Description',
      value: isEditing ? renderDraftField('description', { multiline: true }) : (project.Description || 'Not set'),
      wrap: true,
      className: isEditing ? undefined : 'project-description-row',
    },
    ...(isEditing ? [{ label: 'Project Name', value: renderDraftField('projectName') }] : []),
    {
      label: 'Status',
      value: isEditing
        ? renderDraftField('status', { selectOptions: PROJECT_STATUS_OPTIONS })
        : (project.Status ? <StatusBadge status={project.Status} /> : 'Not set'),
    },
    { label: 'Site Address', value: isEditing ? renderDraftField('siteAddress') : (project.SiteAddress || 'Not set') },
    {
      label: 'Suburb',
      value: isEditing ? (
        <div style={{ display: 'grid', gap: '8px' }}>
          <input
            list="project-detail-suburb-options"
            value={draft?.suburb || ''}
            onChange={(event) => {
              setDraftField('suburb', event.target.value);
              setSuburbLookupMessage('');
            }}
            onBlur={(event) => { void hydrateSuburbLookup(event.target.value); }}
            style={inputStyle}
          />
          <datalist id="project-detail-suburb-options">
            {suburbOptions.map((option) => (
              <option key={`${option.Suburb}-${option.PostCode}`} value={option.Suburb}>
                {option.PostCode}{option.Council ? ` - ${option.Council}` : ''}
              </option>
            ))}
          </datalist>
          {suburbLookupMessage ? (
            <div className="dgf-inline-help">
              {suburbLookupMessage}
            </div>
          ) : null}
        </div>
      ) : (formatSuburbLabel(project.Suburb) || 'Not set'),
    },
    {
      label: 'Postcode',
      value: isEditing ? (
        <input
          value={draft?.postCode || ''}
          onChange={(event) => {
            setDraftField('postCode', event.target.value);
            setSuburbLookupMessage('');
          }}
          onBlur={(event) => { void hydratePostCodeLookup(event.target.value); }}
          style={inputStyle}
        />
      ) : formatFieldValue(project.PostCode),
    },
    {
      label: 'Council',
      value: isEditing ? (
        <div style={{ display: 'grid', gap: '8px' }}>
          <input
            list="project-detail-council-options"
            value={draft?.council || ''}
            onChange={(event) => setDraftField('council', event.target.value)}
            style={inputStyle}
          />
          <datalist id="project-detail-council-options">
            {councilOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        </div>
      ) : (project.Council || 'Not set'),
    },
    {
      label: 'Client Email',
      value: isEditing ? renderDraftField('clientEmail', { type: 'email' }) : (project.ClientEmail || 'Not set'),
      wrap: true,
    },
    {
      label: 'Client Phone',
      value: isEditing ? renderDraftField('clientPhone', { type: 'tel' }) : (project.ClientPhone || project.ClientMobile || 'Not set'),
      wrap: true,
    },
    {
      label: 'Council Engineer Email',
      value: isEditing ? renderDraftField('councilEngineerEmail', { type: 'email' }) : (project.CouncilEngineerEmail || 'Not set'),
      wrap: true,
    },
    { label: 'Last Council Trace', value: formatFieldValue(project.CouncilDrainageEmailLastSentAt) },
    { label: 'Last Council Trace By', value: project.CouncilDrainageEmailLastSentBy || 'Not set', wrap: true },
    { label: 'Site ID', value: isEditing ? renderDraftField('siteId') : (project.SiteId || 'Not set'), wrap: true },
    { label: 'Drive ID', value: isEditing ? renderDraftField('driveId') : (project.DriveId || 'Not set'), wrap: true },
    { label: 'Folder Item ID', value: isEditing ? renderDraftField('folderItemId') : (project.FolderItemId || 'Not set'), wrap: true },
    { label: 'Folder Path', value: isEditing ? renderDraftField('folderPath', { multiline: true }) : (project.FolderPath || 'Not set'), wrap: true },
    { label: 'Created At', value: formatFieldValue(project.CreatedAt) },
    { label: 'Updated At', value: formatFieldValue(project.UpdatedAt) },
  ] : [];

  return (
    <div
      className="dgf-page"
    >
      <div className="dgf-detail-header">
        <div className="dgf-detail-heading">
          <button className="dgf-detail-back" aria-label="Back to projects" onClick={() => navigate('/projects')}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <h1>{project ? `Project ${project.ProjectCode || projectId}` : `Project ${projectId || 'Details'}`}</h1>
        </div>

        {!loading && !error && project ? (
          <div className={`dgf-detail-actions${isEditing ? '' : ' dgf-detail-actions--primary'}`}>
            {isEditing ? (
              <>
                <Button size="sm" variant="secondary" onClick={handleCancelEditing} disabled={saving || deleting}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" onClick={handleSaveProject} disabled={saving || deleting}>
                  {saving ? 'Saving...' : 'Save Project'}
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="secondary" aria-label="Edit Project" iconBefore={<Pencil size={18} aria-hidden="true" />}
                  onClick={handleStartEditing} disabled={deleting || folderProvisioning || folderRefreshing || restoring}>
                  Edit
                </Button>
                <Button size="sm" variant="secondary" aria-label="Central project evidence" iconBefore={<FileSearch size={18} aria-hidden="true" />}
                  onClick={() => setCentralEvidenceOpen(true)} disabled={deleting || folderProvisioning || restoring}>
                  Evidence
                </Button>
                {canViewDocumentStatus ? (
                  <Button size="sm" variant="secondary" aria-label="Project Status" iconBefore={<FileSearch size={18} aria-hidden="true" />}
                    onClick={() => setProjectDocumentStatusOpen(true)} disabled={deleting || folderProvisioning || restoring}>
                    Status
                  </Button>
                ) : null}
                {canNotifyProgress ? (
                  <Button size="sm" variant="secondary" aria-label="Notify Progress" iconBefore={<BellRing size={18} aria-hidden="true" />}
                    onClick={() => navigate(`/projects/${project.ProjectId}/client-updates`)} disabled={deleting || folderProvisioning || restoring}>
                    Notify
                  </Button>
                ) : null}
                <Button size="sm" variant="secondary" onClick={() => { window.location.href = localProjectFolderUrl(Number(project.ProjectId)); }}>
                  Open local folder
                </Button>
                <DetailActionMenu label="More project actions" items={[
                  {
                    label: statusPublishing ? 'Publishing Folder Status...' : 'Publish Folder Status',
                    icon: <RefreshCw aria-hidden="true" />,
                    disabled: statusPublishing || !project.DriveId || !project.FolderItemId || projectIsArchived,
                    onClick: () => {
                      setStatusPublishing(true); setSaveError(null); setSaveNotice(null);
                      void refreshProjectFolderStatus(Number(project.ProjectId))
                        .then(() => setSaveNotice('Project status published to 1-Project Documents/7-AIPM/pmapp-project.json. LE_Admin updates the local folder after OneDrive synchronizes it.'))
                        .catch(() => setSaveError('Folder status could not be published. Check your project access and folder link, then retry.'))
                        .finally(() => setStatusPublishing(false));
                    },
                  },
                  ...(projectIsArchived ? [{
                    label: restoring ? 'Restoring Project...' : 'Restore Project',
                    icon: <RotateCcw aria-hidden="true" />,
                    onClick: handleRestoreProject,
                    disabled: deleting || folderProvisioning || folderRefreshing || restoring,
                  }] : []),
                  {
                    label: folderRefreshing ? 'Refreshing Folder Link...' : 'Refresh Folder Link',
                    icon: <RefreshCw aria-hidden="true" />,
                    onClick: handleRefreshProjectFolder,
                    disabled: deleting || folderProvisioning || folderRefreshing || restoring || !project.DriveId || !project.FolderItemId,
                  },
                  {
                    label: folderProvisioning ? 'Creating Project Folder...' : 'Create Project Folder',
                    icon: <FolderPlus aria-hidden="true" />,
                    onClick: handleProvisionProjectFolder,
                    disabled: deleting || folderProvisioning || folderRefreshing || restoring,
                  },
                  {
                    label: deleting ? 'Deleting...' : 'Delete Project',
                    icon: <Trash2 aria-hidden="true" />,
                    onClick: handleDeleteProject,
                    disabled: deleting || folderProvisioning || folderRefreshing || restoring,
                    danger: true,
                  },
                ]} />
              </>
            )}
          </div>
        ) : null}
      </div>

      {loading && (
        <div className="dgf-page__content">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">⏳</div>
            <div>Loading project details...</div>
          </div>
        </div>
      )}

      {error && (
        <div className="dgf-page__content">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">❌</div>
            <div className="dgf-state-card__title">Error</div>
            <div className="dgf-notice dgf-notice--danger" style={{ marginTop: 16 }}>{error}</div>
            <Button onClick={() => void loadProjectDetails()} variant="primary" style={{ marginTop: '16px' }}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {!loading && !error && project && (
        <div className="dgf-page__content">
          {projectIsArchived ? (
            <div className="dgf-notice dgf-notice--info">
              This project is currently archived or soft-deleted. Restore it to return it to the active projects list and resume normal workflows.
            </div>
          ) : null}
          <ProjectFolderSetupNotice projectId={project.ProjectId} enabled={canNotifyProgress} refreshKey={folderSetupRefresh} onReady={() => { void loadProjectDetails(true); }} />
          {saveError ? (
            <div className="dgf-notice dgf-notice--danger">
              {saveError}
            </div>
          ) : null}

          {saveNotice ? (
            <div className="dgf-notice dgf-notice--success">
              {saveNotice}
            </div>
          ) : null}

          <FormSection
            title="Project Information"
            icon="🏢"
            accentColor="var(--color-primary)"
          >
            <DetailFieldList fields={detailFields} className={isEditing ? '' : 'ui-detail-field-list--inline'} />
          </FormSection>

          {!projectIsArchived && !isEditing ? (
            <ProjectMemoryPanel projectId={Number(project.ProjectId)} projectNumber={String(project.ProjectCode || project.ProjectNumber || '')} />
          ) : null}

          <FormSection
            title="BYDA Lodgement"
            description="Capture the enquiry polygon for this project and send it through the PMApp BYDA workflow after review and confirmation."
            icon="📍"
            accentColor="var(--color-accent, #237b4b)"
            actions={(
              <StatusBadge status={bydaStatusTone}>
                {bydaSubmitting ? 'Submitting' : `${bydaEnquiries.length} logged`}
              </StatusBadge>
            )}
            style={{ marginTop: '20px' }}
          >
            <div style={{ display: 'grid', gap: '20px' }}>
              <BydaUsageSummary
                policy={bydaPolicy}
                heading="Usage limits"
                subheading="WD, JP, and YL are capped at 3 BYDA enquiry calls each per day, with a shared team cap of 10 per day and 50 per month."
              />

              <div
                style={{
                  display: 'grid',
                  gap: '16px',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                }}
              >
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>User reference</span>
                  <input
                    aria-label="User reference"
                    value={bydaDraft?.userReference || ''}
                    onChange={(event) => setBydaField('userReference', event.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Dig start</span>
                  <input
                    aria-label="Dig start"
                    type="date"
                    value={bydaDraft?.digStartAt || ''}
                    onChange={(event) => setBydaField('digStartAt', event.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Dig end</span>
                  <input
                    aria-label="Dig end"
                    type="date"
                    value={bydaDraft?.digEndAt || ''}
                    onChange={(event) => setBydaField('digEndAt', event.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Timezone</span>
                  <input
                    aria-label="Timezone"
                    value={bydaDraft?.userTimezone || ''}
                    onChange={(event) => setBydaField('userTimezone', event.target.value)}
                    style={inputStyle}
                  />
                </label>
                <Select
                  label="Enquiry type"
                  aria-label="BYDA enquiry type"
                  value={bydaDraft?.isPlanningJob ? 'planning' : 'excavation'}
                  onChange={(event) => setBydaField('isPlanningJob', event.target.value === 'planning')}
                  options={[
                    { value: 'planning', label: 'Design' },
                    { value: 'excavation', label: 'Excavation' },
                  ]}
                  helpText="Default to Design unless excavation is actually scheduled."
                  fullWidth
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gap: '16px',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                }}
              >
                <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: '8px' }}>
                  <legend style={{ fontWeight: 600 }}>Activity types</legend>
                  {bydaFormOptionsLoading ? (
                    <div className="dgf-inline-help">Loading BYDA activity options...</div>
                  ) : bydaFormOptions?.activityTypes?.length ? (
                    <div style={bydaOptionGridStyle}>
                      {bydaFormOptions.activityTypes.map((option) => (
                        <label key={option.code} style={bydaOptionCardStyle}>
                          <input
                            type="checkbox"
                            aria-label={formatBydaOptionLabel(option)}
                            checked={hasBydaSelection(bydaDraft?.activityTypes || [], option.code)}
                            onChange={(event) => updateBydaCodeSelection('activityTypes', option.code, event.target.checked)}
                          />
                          <span>{formatBydaOptionLabel(option)}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="dgf-inline-help">No BYDA activity options are available for this enquiry type.</div>
                  )}
                  <span className="dgf-inline-help">
                    {bydaFormOptions?.activityDomain
                      ? `PMApp is using the official BYDA domain ${bydaFormOptions.activityDomain} for this enquiry type.`
                      : 'Choose one or more activities that match the selected enquiry type.'}
                  </span>
                </fieldset>
                <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: '8px' }}>
                  <legend style={{ fontWeight: 600 }}>Workplace location(s)</legend>
                  <div style={bydaOptionGridStyle}>
                    {BYDA_WORKPLACE_OPTIONS.map((option) => (
                      <label key={option.code} style={bydaOptionCardStyle}>
                        <input
                          type="checkbox"
                          aria-label={option.label}
                          checked={hasBydaSelection(bydaDraft?.workplaceSelections || [], option.code)}
                          onChange={(event) => updateBydaWorkplaceSelection(option.code, event.target.checked)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  <span className="dgf-inline-help">Tick every workplace surface that applies to this enquiry area.</span>
                </fieldset>
              </div>

              {bydaFormOptionsError ? (
                <div className="dgf-notice dgf-notice--danger">Failed to load BYDA options: {bydaFormOptionsError}</div>
              ) : null}

              <label style={{ display: 'grid', gap: '6px' }}>
                <span>Working on behalf of</span>
                <select
                  aria-label="BYDA working on behalf of"
                  value={bydaDraft?.authorityId || ''}
                  onChange={(event) => setBydaField('authorityId', event.target.value)}
                  style={inputStyle}
                >
                  <option value="">Private</option>
                  {(bydaFormOptions?.authorityOptions || []).map((option) => (
                    <option key={option.id} value={String(option.id)}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="dgf-inline-help">
                  {bydaFormOptions?.authorityOptionsWarning
                    ? bydaFormOptions.authorityOptionsWarning
                    : bydaDraftShape
                      ? 'Matching organisations are loaded from BYDA for the current enquiry polygon.'
                      : 'Draw the enquiry area to load matching organisations. Leave as Private if you are not working on behalf of another organisation.'}
                </span>
              </label>

              <label style={{ display: 'grid', gap: '6px' }}>
                <span>Notes</span>
                <textarea
                  aria-label="BYDA notes"
                  value={bydaDraft?.notes || ''}
                  onChange={(event) => setBydaField('notes', event.target.value)}
                  style={textareaStyle}
                />
              </label>

              <div
                style={{
                  display: 'grid',
                  gap: '16px',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                }}
              >
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Address line 1</span>
                  <input
                    aria-label="Address line 1"
                    value={bydaDraft?.addressLine1 || ''}
                    onChange={(event) => setBydaField('addressLine1', event.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Locality</span>
                  <input
                    aria-label="Locality"
                    value={bydaDraft?.locality || ''}
                    onChange={(event) => setBydaField('locality', event.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Postcode</span>
                  <input
                    aria-label="BYDA postcode"
                    value={bydaDraft?.postcode || ''}
                    onChange={(event) => setBydaField('postcode', event.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>State</span>
                  <input
                    aria-label="State"
                    value={bydaDraft?.state || ''}
                    onChange={(event) => setBydaField('state', event.target.value)}
                    style={inputStyle}
                  />
                </label>
              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(bydaDraft?.isSandboxTest)}
                    onChange={(event) => setBydaField('isSandboxTest', event.target.checked)}
                  />
                  <span>Sandbox test</span>
                </label>
              </div>

              <div
                style={{
                  display: 'grid',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <strong>Enquiry area</strong>
                    <div className="dgf-inline-help">Use the map to draw the BYDA polygon instead of typing raw coordinates.</div>
                  </div>
                  <Button variant="secondary" onClick={resetBydaDraft} disabled={bydaSubmitting}>
                    Reset Form
                  </Button>
                </div>

                <BydaMapCapture
                  points={bydaDraft?.points || []}
                  addressQuery={[
                    bydaDraft?.addressLine1 || '',
                    bydaDraft?.locality || '',
                    bydaDraft?.postcode || '',
                    bydaDraft?.state || '',
                  ].filter(Boolean).join(', ')}
                  autoLocateKey={bydaAutoLocateKey || null}
                  disabled={projectIsArchived || bydaSubmitting}
                  onChange={setBydaPoints}
                />
              </div>

              {bydaSubmitError ? (
                <div className="dgf-notice dgf-notice--danger">{bydaSubmitError}</div>
              ) : null}
              {bydaSubmitNotice ? (
                <div className="dgf-notice dgf-notice--success">{bydaSubmitNotice}</div>
              ) : null}
              {bydaLoadError ? (
                <div className="dgf-notice dgf-notice--danger">{bydaLoadError}</div>
              ) : null}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  onClick={handleSubmitBydaEnquiry}
                  disabled={projectIsArchived || bydaSubmitting || bydaFormOptionsLoading || Boolean(bydaFormOptionsError)}
                >
                  {bydaSubmitting ? 'Preparing BYDA...' : 'Review BYDA Request'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void loadBydaEnquiries(project.ProjectId)}
                  disabled={bydaSubmitting || bydaLoading}
                >
                  {bydaLoading ? 'Refreshing Enquiries...' : 'Refresh Enquiries'}
                </Button>
              </div>

              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <strong>Recent BYDA enquiries</strong>
                  <span className="dgf-inline-help">Select an enquiry to inspect its returned files and sync trail.</span>
                </div>
                {bydaLoading && bydaEnquiries.length === 0 ? (
                  <div className="dgf-inline-help">Loading BYDA enquiries...</div>
                ) : bydaEnquiries.length === 0 ? (
                  <div className="dgf-inline-help">No BYDA enquiries logged for this project yet.</div>
                ) : (
                  bydaEnquiries.map((item) => (
                    <div
                      key={item.bydaEnquiryId}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedBydaEnquiryId(item.bydaEnquiryId)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedBydaEnquiryId(item.bydaEnquiryId);
                        }
                      }}
                      style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: '14px',
                        padding: '14px 16px',
                        background: selectedBydaEnquiryId === item.bydaEnquiryId ? 'rgba(23, 59, 132, 0.08)' : 'var(--color-surface)',
                        display: 'grid',
                        gap: '8px',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                        <strong>{item.userReference || item.project?.projectNumber || `BYDA ${item.bydaEnquiryId}`}</strong>
                        <StatusBadge status={item.localStatus || 'submitted'}>
                          {item.localStatus || 'submitted'}
                        </StatusBadge>
                      </div>
                      <div className="dgf-inline-help">
                        {`${formatBydaDisplayDate(item.digStartAt) || 'Unknown start'} to ${formatBydaDisplayDate(item.digEndAt) || 'Unknown end'}`}
                        {item.remoteEnquiryId ? ` · Remote #${item.remoteEnquiryId}` : ''}
                        {item.remoteExternalId ? ` · SYS-ID ${item.remoteExternalId}` : ''}
                      </div>
                      <div className="dgf-inline-help">
                        {item.activityTypes?.length ? `Activities: ${item.activityTypes.join(', ')}` : 'No activity types recorded'}
                      </div>
                      {item.shareUrl ? (
                        <a href={item.shareUrl} target="_blank" rel="noreferrer">
                          Open BYDA share link
                        </a>
                      ) : null}
                      {item.source === 'PMAPP_AUTO' && item.localStatus === 'needs_review' && bydaPolicy?.actor.isTracked &&
                        (!item.createdBy || item.createdBy === bydaPolicy.actor.email) && (
                        <Button variant="secondary" onClick={async event => {
                          event.stopPropagation();
                          if (!window.confirm('Take responsibility for this DBYD enquiry and submit with your BYDA account if Vicmap confirms one property?')) return;
                          try {
                            await bydaService.claimAcceptance(item.bydaEnquiryId);
                            await loadBydaEnquiries(project.ProjectId);
                          } catch (error) { setBydaLoadError(error instanceof Error ? error.message : 'Could not queue DBYD.'); }
                        }}>Use my BYDA account and retry</Button>
                      )}
                      {item.lastErrorMessage ? (
                        <div className="dgf-inline-help" style={{ color: 'var(--color-danger, #b42318)' }}>
                          {item.lastErrorMessage}
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>

              {bydaSelectedRecord ? (
                <div
                  style={{
                    display: 'grid',
                    gap: '16px',
                    marginTop: '8px',
                  }}
                >
                  <div
                    style={{
                      border: '1px solid var(--color-border)',
                      borderRadius: '16px',
                      padding: '16px',
                      background: 'linear-gradient(180deg, rgba(23, 59, 132, 0.05) 0%, rgba(255,255,255,0.98) 100%)',
                      display: 'grid',
                      gap: '10px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ display: 'grid', gap: '4px' }}>
                        <strong>{bydaSelectedRecord.project?.projectNumber || bydaSelectedRecord.userReference || `BYDA ${bydaSelectedRecord.bydaEnquiryId}`}</strong>
                        <div className="dgf-inline-help">
                          {bydaSelectedRecord.project?.projectName || bydaSelectedRecord.project?.siteAddress || 'Project details pending'}
                        </div>
                      </div>
                      <StatusBadge status={bydaSelectedRecord.localStatus || 'submitted'}>
                        {formatBydaStatusLabel(bydaSelectedRecord.localStatus)}
                      </StatusBadge>
                    </div>
                    <div className="dgf-inline-help">
                      {bydaSelectedRecord.remoteEnquiryId ? `Remote #${bydaSelectedRecord.remoteEnquiryId}` : 'Remote enquiry id pending'}
                      {bydaSelectedRecord.remoteExternalId ? ` · SYS-ID ${bydaSelectedRecord.remoteExternalId}` : ''}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <Button
                        variant="secondary"
                        onClick={() => void loadBydaDetail(bydaSelectedRecord.bydaEnquiryId, { refreshRemote: true })}
                        disabled={bydaDetailLoading}
                      >
                        {bydaDetailLoading ? 'Refreshing...' : 'Refresh Selected Enquiry'}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleBydaFileResults}
                        disabled={bydaFiling || bydaDetailLoading || bydaResultDocuments.length === 0}
                      >
                        {bydaFiling ? 'Uploading To SharePoint...' : 'Upload Results To Project Folder'}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleFetchBydaYvwSewerInfo}
                        disabled={bydaYvwSewerLoading || bydaDetailLoading}
                      >
                        {bydaYvwSewerLoading ? 'Fetching Sewer Info...' : 'Fetch YVW Sewer Info'}
                      </Button>
                      {bydaSelectedRecord.shareUrl ? (
                        <a href={bydaSelectedRecord.shareUrl} target="_blank" rel="noreferrer">
                          Open BYDA share link
                        </a>
                      ) : null}
                    </div>

                    {bydaFilingFeedback ? (
                      <div className={`dgf-notice ${bydaFilingFeedbackTone === 'error' ? 'dgf-notice--danger' : 'dgf-notice--success'}`}>
                        {bydaFilingFeedback}
                      </div>
                    ) : null}
                    {bydaDetailError ? (
                      <div className="dgf-notice dgf-notice--danger">{bydaDetailError}</div>
                    ) : null}
                    {bydaYvwSewerError ? (
                      <div className="dgf-notice dgf-notice--danger">YVW sewer lookup failed: {bydaYvwSewerError}</div>
                    ) : null}
                    {bydaDetail?.remoteSyncError ? (
                      <div className="dgf-notice dgf-notice--danger">
                        Remote refresh warning: {bydaDetail.remoteSyncError.message}
                      </div>
                    ) : null}
                    {bydaDetail?.resultWarnings?.length ? (
                      <div className="dgf-inline-help">{bydaDetail.resultWarnings.join(' ')}</div>
                    ) : null}
                    <div className="dgf-inline-help">
                      {bydaDetail?.sharePointTarget?.folderPath
                        ? `Project folder target: ${bydaDetail.sharePointTarget.folderPath}`
                        : 'Project SharePoint target has not been resolved yet.'}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gap: '16px',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    }}
                  >
                    <div
                      style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: '16px',
                        padding: '16px',
                        background: 'var(--color-surface)',
                        display: 'grid',
                        gap: '12px',
                        alignContent: 'start',
                      }}
                    >
                      <div>
                        <strong>Response documents</strong>
                        <div className="dgf-inline-help">Returned PDFs and attachments discovered from BYDA for this enquiry.</div>
                      </div>
                      {bydaDetailLoading && bydaResultDocuments.length === 0 ? (
                        <BydaResultFileList
                          documents={bydaResultDocuments}
                          loading={bydaDetailLoading}
                          saving={bydaFiling}
                          onPreviewDocument={setBydaPreviewDocument}
                          onSaveToSharePoint={handleBydaFileResults}
                          formatStatus={formatBydaStatusLabel}
                          formatDateTime={formatBydaDateTime}
                        />
                      ) : bydaResultDocuments.length === 0 ? (
                        <div className="dgf-inline-help">No response documents have been discovered for this enquiry yet.</div>
                      ) : (
                        <BydaResultFileList
                          documents={bydaResultDocuments}
                          saving={bydaFiling}
                          onPreviewDocument={setBydaPreviewDocument}
                          onSaveToSharePoint={handleBydaFileResults}
                          formatStatus={formatBydaStatusLabel}
                          formatDateTime={formatBydaDateTime}
                        />
                      )}
                    </div>

                    <BydaResultPdfPreview document={bydaPreviewDocument} onClose={() => setBydaPreviewDocument(null)} />
                    <BydaYvwSewerInfoDialog info={bydaYvwSewerInfo} onClose={() => setBydaYvwSewerInfo(null)} />

                    <div
                      style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: '16px',
                        padding: '16px',
                        background: 'var(--color-surface)',
                        display: 'grid',
                        gap: '12px',
                        alignContent: 'start',
                      }}
                    >
                      <div>
                        <strong>Webhook activity</strong>
                        <div className="dgf-inline-help">Recent signed BYDA sync events captured by PMApp for this enquiry.</div>
                      </div>
                      {bydaDetailLoading && bydaWebhookEvents.length === 0 ? (
                        <div className="dgf-inline-help">Loading webhook activity...</div>
                      ) : bydaWebhookEvents.length === 0 ? (
                        <div className="dgf-inline-help">No webhook activity has been logged for this enquiry yet.</div>
                      ) : (
                        bydaWebhookEvents.map((event) => (
                          <div
                            key={event.bydaWebhookEventId}
                            style={{
                              border: '1px solid rgba(23, 59, 132, 0.12)',
                              borderRadius: '14px',
                              padding: '12px 14px',
                              background: 'rgba(255,255,255,0.96)',
                              display: 'grid',
                              gap: '8px',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                              <strong>{formatBydaStatusLabel(event.eventType)}</strong>
                              <StatusBadge status={event.lastOutcome || 'received'}>
                                {formatBydaStatusLabel(event.lastOutcome)}
                              </StatusBadge>
                            </div>
                            <div className="dgf-inline-help">{formatBydaDateTime(event.createdDate)}</div>
                            <div className="dgf-inline-help">
                              {event.remoteEnquiryId ? `Remote #${event.remoteEnquiryId}` : 'Remote enquiry id pending'}
                              {event.userReference ? ` · ${event.userReference}` : ''}
                            </div>
                            {event.processingNotes ? (
                              <div className="dgf-inline-help">{event.processingNotes}</div>
                            ) : null}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </FormSection>
        </div>
      )}

      {project && centralEvidenceOpen ? <Modal open={centralEvidenceOpen} onClose={() => setCentralEvidenceOpen(false)} title={`Project evidence · ${project.ProjectCode || project.ProjectNumber || ''}`} size="lg">
        <ProjectManagementEvidence projectId={Number(project.ProjectId || projectId)} projectLabel={String(project.ProjectCode || project.ProjectNumber || '')} />
      </Modal> : null}

      {project ? (
        <ProjectDocumentStatusPanel
          open={projectDocumentStatusOpen}
          onClose={() => setProjectDocumentStatusOpen(false)}
          projectId={Number(project.ProjectId || projectId)}
          projectNumber={String(project.ProjectCode || project.ProjectNumber || '')}
        />
      ) : null}

      <div style={{ height: '80px' }}></div>
    </div>
  );
};

export default ProjectDetail;
